import { useState } from 'react'

import { Menu, MenuItem } from '@/components/interactives/dropdown-menu.js'
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import {
  faCheck,
  faCheckDouble,
  faClock,
  faPencil,
  faWarning,
} from '@fortawesome/free-solid-svg-icons'

import { cn } from '@/utils'
import { useAtom } from 'jotai'

import { RundownPermission, RundownStatus } from '~shared/types'

import { rundownAtom } from '@/store/rundown.store.js'

import { updateRundown } from '../../../firestore.js'
import { isValueInEnum, objectEntries } from '../../../utils/primitives.ts'
import { useRundownAccess } from '../../permissions/lib/use-rundown-access.js'

// `template` is toggled from the dashboard card
type StatusId = Exclude<
  RundownStatus,
  RundownStatus.IMPORTED | RundownStatus.TEMPLATE
>

type StatusItemConfig = {
  name: string
  icon: IconDefinition
  css: string
  itemCss: string
}

type StatusItems = Record<StatusId, StatusItemConfig>

const STATUS_ITEMS = {
  draft: {
    name: 'Draft',
    icon: faPencil,
    css: 'text-gray-400!',
    itemCss: 'text-gray-500 bg-gray-950',
  },
  'awaiting-data': {
    name: 'Awaiting data',
    icon: faClock,
    css: 'text-orange-500!',
    itemCss: 'text-orange-500 bg-orange-900',
  },
  approved: {
    name: 'Approved',
    icon: faCheck,
    css: 'text-green-500!',
    itemCss: 'text-green-500 bg-green-900',
  },
  finalized: {
    name: 'Finalized',
    icon: faCheckDouble,
    css: 'text-blue-500!',
    itemCss: 'text-blue-500 bg-blue-800',
  },
  rejected: {
    name: 'Rejected',
    icon: faWarning,
    css: 'text-red-500!',
    itemCss: 'text-red-500 bg-red-900',
  },
} as const satisfies StatusItems

function isValidStatus(value: unknown): value is RundownStatus {
  return isValueInEnum(value, RundownStatus)
}

export function RundownNavbarStatusMenu() {
  const [loading, setLoading] = useState(false)

  const [rundown, setRundown] = useAtom(rundownAtom)

  const { hasPermission } = useRundownAccess()
  const canEdit = hasPermission(RundownPermission.EDIT)

  const updateStatus = async (status: string) => {
    if (!rundown) return
    if (!isValidStatus(status)) return

    setLoading(true)

    await updateRundown(rundown.id, { status })
    setRundown({ ...rundown, status: status })

    setLoading(false)
  }

  return (
    <Menu
      loading={loading}
      disabled={!canEdit}
      label={STATUS_ITEMS[rundown?.status || 'draft']?.name || rundown?.status}
      className={cn(
        'h-auto! px-1! py-0!',
        'border border-current rounded-sm',
        'text-xs whitespace-nowrap',
        'disabled:pointer-events-none',
        STATUS_ITEMS[rundown?.status || 'draft']?.css,
      )}
    >
      {objectEntries(STATUS_ITEMS).map(([statusId, statusConfig]) => (
        <MenuItem
          key={statusConfig.name}
          icon={statusConfig.icon}
          label={statusConfig.name}
          onClick={() => updateStatus(statusId)}
          className={cn('mb-1', statusConfig.itemCss)}
        />
      ))}
    </Menu>
  )
}
